import { Fragment, useCallback, useEffect, useState } from 'react';
import { Client, WorkspaceUser } from '@zappar/zw-api-client';
import { ZprDropdownMenu } from '@zpr-stencil';

import { IAsset } from '../providers';
import { useAssetActions } from '../context/AssetActionsContext';
import { useEchoContext } from '../echo/EchoContext';
import { isSystemKind } from '../echo/systemMessages';
import { ProcessingIcon, BinIcon } from '../../../icons';
import { ActionsIcon } from '../../../icons/ActionsIcon';

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const toDateFromEpoch = (value: number): Date => {
  // Accept both seconds and milliseconds epoch values.
  if (value < 1_000_000_000_000) {
    return new Date(value * 1000);
  }
  return new Date(value);
};

const formatCommentTime = (value: number): string => {
  const date = toDateFromEpoch(value);
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const formatCommentDateDivider = (value: number): string => {
  const date = toDateFromEpoch(value);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const isSameDay = (left: number, right: number): boolean => {
  const leftDate = toDateFromEpoch(left);
  const rightDate = toDateFromEpoch(right);

  return (
    leftDate.getFullYear() === rightDate.getFullYear()
    && leftDate.getMonth() === rightDate.getMonth()
    && leftDate.getDate() === rightDate.getDate()
  );
};

export const CommentsTab = ({ asset, zwApiClient }: { asset: IAsset; zwApiClient: Client }) => {
  const { workspaceId } = useAssetActions();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [openActionsCommentId, setOpenActionsCommentId] = useState<string | null>(null);
  const { comments, isLoadingComments: isLoading, isConnected, error, postComment, deleteComment, subscribeToAsset, unsubscribeFromAsset } = useEchoContext();

  useEffect(() => {
    subscribeToAsset(asset);
    return () => unsubscribeFromAsset(asset);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.getId()]);

  useEffect(() => {
    zwApiClient.getUser().then(u => setCurrentUserId(u.id)).catch(() => {});
  }, [zwApiClient]);

  useEffect(() => {
    if (!workspaceId) return;
    zwApiClient.getWorkspaceUsers(workspaceId).then(setUsers).catch(() => {});
  }, [zwApiClient, workspaceId]);

  const handleScrollContainer = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const isAtScrollTop = target.scrollTop === 0;
    if (isAtScrollTop) {
      setHasScrolled(false);
    } else {
      setHasScrolled(true);
    }
  };

  const getWorkspaceUser = (userId: string): WorkspaceUser | undefined =>
    users.find(u => u.id === userId);

  const handleAddComment = async () => {
    if (!newComment.trim() || !isConnected) return;

    setIsSubmitting(true);
    try {
      await postComment(newComment.trim());
      setNewComment('');
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = useCallback(async (commentId: string) => {
    try {
      await deleteComment(commentId);
      setOpenActionsCommentId(prev => (prev === commentId ? null : prev));
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  }, [deleteComment]);

  useEffect(() => {
    // zpr-dropdown moves slotted body content outside the React root; use native delegation.
    const handleNativeDelete = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const button = target?.closest<HTMLButtonElement>('button[data-comment-delete-id]');
      const commentId = button?.dataset.commentDeleteId;
      if (!commentId) return;

      event.preventDefault();
      event.stopPropagation();
      void handleDeleteComment(commentId);
    };

    document.addEventListener('mousedown', handleNativeDelete, true);
    return () => {
      document.removeEventListener('mousedown', handleNativeDelete, true);
    };
  }, [handleDeleteComment]);

  const handleActionsMenuOpenChange = (commentId: string, event: CustomEvent<boolean | { isOpen?: boolean }>) => {
    const detail = event.detail;
    const isOpen = typeof detail === 'boolean' ? detail : !!detail?.isOpen;

    setOpenActionsCommentId(prev => {
      if (isOpen) return commentId;
      return prev === commentId ? null : prev;
    });
  };

  return (
    <div className="comments-section">
      <div className={`comments-list ${hasScrolled ? 'scrolled' : ''}`} onScroll={handleScrollContainer}>
        {isLoading ? (
          <p className="comments-empty">
            <ProcessingIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
          </p>
        ) : error ? (
          <p className="comments-empty">{error}</p>
        ) : comments.length === 0 ? (
          <p className="comments-empty">No comments yet. <br /> Be the first to comment!</p>
        ) : (
          <ul>
            {comments.map((comment, index) => {
              const user = getWorkspaceUser(comment.userId);
              const displayName = user?.name ?? comment.userId;
              const previousComment = comments[index - 1];
              const shouldShowDateDivider = !previousComment || !isSameDay(previousComment.createdAt, comment.createdAt);
              const isSystem = isSystemKind(comment.kind);

              if (isSystem) {
                return (
                  <Fragment key={comment.commentId}>
                    {shouldShowDateDivider && (
                      <li className="comments-date-divider" aria-label={`Date ${formatCommentDateDivider(comment.createdAt)}`}>
                        <span>{formatCommentDateDivider(comment.createdAt)}</span>
                      </li>
                    )}
                    <li className="comment-item is-system">
                      <div className="comment-content">
                        <p className="comment-text">
                          <span className="comment-author">{displayName}</span>
                          {' '}
                          {comment.body}
                          {' · '}
                          <span className="comment-date">{formatCommentTime(comment.createdAt)}</span>
                        </p>
                      </div>
                    </li>
                  </Fragment>
                );
              }

              return (
                <Fragment key={comment.commentId}>
                  {shouldShowDateDivider && (
                    <li className="comments-date-divider" aria-label={`Date ${formatCommentDateDivider(comment.createdAt)}`}>
                      <span>{formatCommentDateDivider(comment.createdAt)}</span>
                    </li>
                  )}
                  <li className="comment-item">
                    <div className="comment-avatar">
                      {user?.avatarUrl ? (
                        <img src={user.avatarUrl} alt={displayName} className="comment-avatar-img" />
                      ) : (
                        <div className="comment-avatar-placeholder">
                          {getInitials(displayName)}
                        </div>
                      )}
                    </div>
                    <div className="comment-content">
                      <div className="comment-header">
                        <span className="comment-author">{displayName}</span>
                        <span className="comment-date">{formatCommentTime(comment.createdAt)}</span>
                      </div>
                      <p className="comment-text">{comment.body}</p>
                    </div>
                    {currentUserId === comment.userId && (
                      <ZprDropdownMenu onOpenChanged={(event) => handleActionsMenuOpenChange(comment.commentId, event)} onClick={ev => ev.stopPropagation()}>
                        <div slot="toggle" className={`comment-actions ${openActionsCommentId === comment.commentId ? 'open' : ''}`}>
                          <button className="btn btn-ghost p0 btn-medium" aria-label="Comment actions">
                            <ActionsIcon width={16} height={16} />
                          </button>
                        </div>
                        <ul slot="body" className='comment-actions-list'>
                          <li>
                            <button
                              type="button"
                              className='btn btn-ghost p0'
                              data-comment-delete-id={comment.commentId}
                              aria-label='Delete comment'
                            >
                              <BinIcon width={20} height={20} style={{ margin: 10 }} />
                              Delete comment
                            </button>
                          </li>
                        </ul>
                      </ZprDropdownMenu>
                    )}
                  </li>
                </Fragment>
              );
            })}
          </ul>
        )}
      </div>

      <div className="comment-form">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          aria-label="Comment text"
          disabled={isSubmitting || !isConnected}
          rows={3}
        />
        <button
          type="button"
          className={`btn btn-medium btn-secondary w100 ${isSubmitting ? 'processing' : ''}`}
          onClick={handleAddComment}
          disabled={isSubmitting || !newComment.trim() || !isConnected}
          aria-label="Send comment"
        >
          Send
        </button>
      </div>
    </div>
  );
};
